import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// New logos
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";

// Tyre graphic for hero section
const TYRE_HERO = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [bottomEmail, setBottomEmail] = useState("");
  const [bottomLoading, setBottomLoading] = useState(false);

  const handleEarlyAccess = async (e, isBottom = false) => {
    e.preventDefault();
    const currentEmail = isBottom ? bottomEmail : email;
    const setCurrentLoading = isBottom ? setBottomLoading : setLoading;
    
    if (!currentEmail || !currentEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setCurrentLoading(true);
    try {
      await axios.post(`${API}/early-access`, { email: currentEmail });
      toast.success("You're on the list! We'll notify you when we launch.");
      if (isBottom) {
        setBottomEmail("");
      } else {
        setEmail("");
      }
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info("You're already on the early access list!");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setCurrentLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white overflow-x-hidden">
      <SEO page="comingSoon" />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background glow effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-[150px]"></div>
          <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-orange-500/5 to-transparent"></div>
        </div>

        <div className="container mx-auto px-6 lg:px-12 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Get Tyre Quotes <span className="text-orange-500">•</span>{" "}
                <span className="text-orange-500">Compare</span>{" "}
                <span className="text-orange-500">•</span>{" "}
                <span className="text-orange-500">Save</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-400 max-w-xl">
                New Zealand's smartest way to compare tyre quotes and book services.
              </p>

              {/* Email Form */}
              <form onSubmit={(e) => handleEarlyAccess(e, false)} className="flex flex-col sm:flex-row gap-3 max-w-lg">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-5 py-4 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                  data-testid="hero-email-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  data-testid="hero-submit-btn"
                >
                  {loading ? "..." : "Get Early Access"}
                </button>
              </form>

              {/* Launch Badge */}
              <div className="inline-flex items-center gap-2 text-orange-400 text-sm mt-4">
                <span className="text-lg">🇳🇿</span>
                <span>Launching Soon — Limited Early Access</span>
              </div>
            </div>

            {/* Right Content - Tyre Graphic */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                {/* Glow effect behind tyre */}
                <div className="absolute inset-0 bg-orange-500/20 blur-[80px] rounded-full scale-75"></div>
                <img
                  src={TYRE_HERO}
                  alt="TyreMatch"
                  className="relative w-full max-w-md lg:max-w-lg xl:max-w-xl drop-shadow-2xl animate-float"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-900 to-transparent"></div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">How It Works</h2>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { num: "01", title: "Request a Quote", desc: "Enter rego or tyre details" },
              { num: "02", title: "Compare Offers", desc: "Get up to 5 quotes nearby" },
              { num: "03", title: "Choose & Save", desc: "Pick the best deal & book" }
            ].map((step) => (
              <div
                key={step.num}
                className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-6 hover:border-orange-500/50 transition-all group"
              >
                <div className="flex items-center gap-4 mb-3">
                  <span className="text-3xl font-bold text-orange-500">{step.num}</span>
                  <h3 className="text-xl font-semibold text-white group-hover:text-orange-400 transition-colors">
                    {step.title}
                  </h3>
                </div>
                <p className="text-gray-400 ml-14">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Choose Your Services Section */}
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Choose Your Services</h2>
          
          <div className="max-w-md mx-auto bg-gray-800/30 border border-gray-700/50 rounded-xl p-8">
            <div className="space-y-4">
              {["Tyres", "Wheel Alignment", "Rotation"].map((service) => (
                <div key={service} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded bg-orange-500/20 border border-orange-500 flex items-center justify-center">
                    <svg className="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-white font-medium">{service}</span>
                </div>
              ))}
            </div>
            <p className="text-orange-400 text-sm text-center mt-6">
              Select one or multiple services for your quote.
            </p>
          </div>
        </div>
      </section>

      {/* Why TyreMatch Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            Why Drivers Choose Tyre<span className="text-orange-500">4</span>You
          </h2>
          
          <div className="max-w-2xl mx-auto space-y-4">
            {[
              "No more calling multiple shops",
              "Transparent pricing from real businesses",
              "Compare quotes side by side",
              "Save time and money"
            ].map((benefit) => (
              <div key={benefit} className="flex items-center gap-4 bg-gray-800/20 rounded-lg p-4">
                <div className="w-8 h-8 rounded-full bg-orange-500/20 border border-orange-500/50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-gray-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center bg-gradient-to-r from-gray-800/50 to-gray-800/30 border border-gray-700/50 rounded-2xl p-8 lg:p-12">
            <div className="text-4xl mb-4">🇳🇿</div>
            <h3 className="text-2xl font-bold mb-4">Launching in New Zealand</h3>
            <p className="text-gray-400 leading-relaxed">
              Our platform has been fully tested end-to-end — from quote requests to shop approvals, 
              order confirmations, and invoicing. We're ready to transform how Kiwis buy tyres.
            </p>
          </div>
        </div>
      </section>

      {/* Email Capture Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Be First to Access</h2>
            <p className="text-gray-400 mb-8">Get early access + exclusive launch offers</p>
            
            <form onSubmit={(e) => handleEarlyAccess(e, true)} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                value={bottomEmail}
                onChange={(e) => setBottomEmail(e.target.value)}
                className="flex-1 px-5 py-4 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                data-testid="bottom-email-input"
              />
              <button
                type="submit"
                disabled={bottomLoading}
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                data-testid="bottom-submit-btn"
              >
                {bottomLoading ? "..." : "Notify Me"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-800">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Logo */}
            <img
              src={LOGO_DARK}
              alt="TyreMatch"
              className="h-16 w-auto"
            />
            
            {/* Contact */}
            <div className="text-right">
              <a 
                href="mailto:sales@tyrematch.co.nz"
                className="text-gray-400 hover:text-orange-500 transition-colors"
              >
                sales@tyrematch.co.nz
              </a>
              <p className="text-gray-600 text-sm mt-2">
                © 2026 TyreMatch. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Custom animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
